import Reveal, { Cross } from "@/components/Reveal";
import { useI18n } from "@/i18n";
import { cvUrl } from "@/lib/api";
import { Download } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1720036237390-7cec3edda585?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function About() {
  const { t } = useI18n();
  return (
    <section id="about" className="relative scroll-mt-20 px-6 py-24 md:px-10 md:py-32 xl:px-16" data-testid="about-section">
      <Cross className="left-4 top-16 hidden md:block" />
      <Cross className="right-4 bottom-16 hidden md:block" />
      <div className="mx-auto grid max-w-[1440px] gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="text-[11px] tracking-[0.3em] text-gold">{t.about.label}</div>
            <h2 className="mt-5 font-display text-3xl font-bold leading-tight text-white md:text-4xl" data-testid="about-title">
              {t.about.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-10">
            <div className="relative overflow-hidden border border-line" data-testid="about-visual">
              <img src={ABOUT_IMG} alt="Industrial plant interior" className="h-64 w-full object-cover md:h-80" data-testid="about-image" />
              <span className="absolute bottom-3 left-3 text-[10px] tracking-[0.25em] text-gold">{t.about.fig}</span>
            </div>
          </Reveal>
          <Reveal delay={0.25} className="mt-6">
            <a
              href={cvUrl()}
              download="CALEB_DEMISEW_ZELEKE_CV.pdf"
              target="_blank"
              rel="noreferrer"
              data-testid="about-cv-btn"
              className="group inline-flex items-center gap-3 border border-gold/40 px-6 py-3 text-xs font-bold tracking-[0.2em] text-gold transition-colors duration-200 hover:bg-gold hover:text-black"
            >
              {t.about.cvBtn}
              <Download size={14} className="transition-transform duration-200 group-hover:translate-y-0.5" />
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-7 lg:pt-4">
          <Reveal delay={0.1}>
            <p className="max-w-2xl text-sm leading-relaxed text-zinc-300 md:text-base" data-testid="about-p1">{t.about.p1}</p>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-zinc-400 md:text-base" data-testid="about-p2">{t.about.p2}</p>
            <p className="mt-6 max-w-2xl border-l-2 border-gold pl-4 text-sm leading-relaxed text-white md:text-base" data-testid="about-p3">{t.about.p3}</p>
          </Reveal>

          <Reveal delay={0.2} className="mt-10 flex flex-wrap gap-3">
            {t.about.tags.map((tag) => (
              <span key={tag} className="border border-line bg-panel px-3 py-2 text-[10px] tracking-[0.25em] text-zinc-300 transition-colors duration-200 hover:border-gold hover:text-gold">
                {tag}
              </span>
            ))}
          </Reveal>

          <Reveal delay={0.3} className="mt-12 border border-line bg-panel" data-testid="about-status">
            <div className="border-b border-line px-5 py-3 text-[10px] tracking-[0.3em] text-gold">PROFILE.SYS</div>
            <div className="divide-y divide-white/5">
              {t.about.status.map((row) => (
                <div key={row.k} className="flex items-center justify-between gap-4 px-5 py-3.5 text-xs tracking-[0.15em]">
                  <span className="text-zinc-500">{row.k}</span>
                  <span className={`text-right ${row.hot ? "font-bold text-gold" : "text-zinc-200"}`}>
                    {row.hot && <span className="mr-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-gold" />}
                    {row.v}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.35} className="mt-6 border border-line bg-panel" data-testid="about-log">
            <div className="border-b border-line px-5 py-3 text-[10px] tracking-[0.3em] text-gold">{t.about.logTitle}</div>
            <div className="divide-y divide-white/5">
              {t.about.log.map((row) => (
                <div key={row.k} className="px-5 py-4">
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-xs font-bold tracking-[0.15em] text-white">{row.k}</span>
                    <span className="shrink-0 text-[10px] tracking-[0.2em] text-gold">{row.d}</span>
                  </div>
                  <p className="mt-1.5 text-[11px] leading-relaxed tracking-[0.05em] text-zinc-500">{row.v}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
